<template>
  <div class="container-settingsview">
    <TabsWithValue :tabs :tab-active-value @tabClicked="handleTabClick" />
    <div class="container-settings" v-show="tabActiveValue === 'SCREEN'">
      <AuxiliaryDisplayControlScreen ref="screenControlRef" :is-global="false" />
    </div>

    <div class="container-settings" v-show="tabActiveValue === 'SUBTITLE'">
      <AuxiliaryDisplayControlSubtitle ref="subtitleControlRef" :is-global="false" />
    </div>

    <div class="footer">
      <ButtonProcess full-width secondary @click="onClickSave">
        {{ t('button_save2') }}
      </ButtonProcess>
      <ButtonProcess full-width @click="onClickApply" :disabled="isDisabledApply">
        {{ t('screen_apply_setting') }}
      </ButtonProcess>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import AuxiliaryDisplayControlScreen from '@/components/domains/chat/chat-general/AuxiliaryDisplayControlScreen.vue';
import AuxiliaryDisplayControlSubtitle from '@/components/domains/chat/chat-general/AuxiliaryDisplayControlSubtitle.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import TabsWithValue from '@/components/shared/composite/TabsWithValue.vue';
import { usePreferences } from '@/composables/shared/usePreferences';
import { useCallStore } from '@/stores/call';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { AuxiliaryDisplay, TabItem } from '@/types/components';

const socketStore = useSocketStore();
const toastStore = useToastStore();
const callStore = useCallStore();
const userStore = useUserStore();
const { t } = useI18n();
const { isValidPreference } = usePreferences();

const tabActiveValue = ref<AuxiliaryDisplay>();
const screenControlRef = useTemplateRef('screenControlRef');
const subtitleControlRef = useTemplateRef('subtitleControlRef');

const mapperAction = {
  SCREEN: {
    storageKey: 'settings_screen_remote',
    attributeName: 'screenAttribute',
    getPayloadFc: () => screenControlRef.value?.payloadMessageFc,
    getAttribute: () => screenControlRef.value?.payloadMessageFc?.screenAttribute,
  },
  SUBTITLE: {
    storageKey: 'settings_subtitle_remote',
    attributeName: 'subtitleAttribute',
    getPayloadFc: () => subtitleControlRef.value?.payloadMessageFc,
    getAttribute: () => subtitleControlRef.value?.payloadMessageFc?.subtitleAttribute,
  },
} as const;

const tabs = computed<TabItem[]>(() => {
  const { screenMode, subtitleMode } = callStore.qrCodeInfo.options;

  const arr: TabItem[] = [];
  if (screenMode) arr.push({ value: 'SCREEN', label: t('label_screen_mode') });
  if (subtitleMode) arr.push({ value: 'SUBTITLE', label: t('label_subtitle_mode') });

  return arr;
});

const isDisabledApply = computed(() => {
  if (tabActiveValue.value === 'SCREEN') {
    return !screenControlRef.value?.payloadMessageFc.screenAttribute?.languageCode;
  } else if (tabActiveValue.value === 'SUBTITLE') {
    return !subtitleControlRef.value?.payloadMessageFc.subtitleAttribute?.languageCode;
  }

  return true;
});

const handleTabClick = (tabClicked: AuxiliaryDisplay) => {
  tabActiveValue.value = tabClicked;
};

const onClickSave = () => saveSettings(tabActiveValue.value);
const onClickApply = () => applySettings(tabActiveValue.value);

const saveSettings = (tab?: AuxiliaryDisplay) => {
  if (!tab) return;

  const { storageKey, getPayloadFc, getAttribute } = mapperAction[tab];
  const payloadFc = getPayloadFc();
  const attribute = getAttribute();

  if (!payloadFc || !isValidPreference(attribute)) return;

  localStorage.setItem(storageKey, JSON.stringify(payloadFc));

  toastStore.info(t('alert_save_completed'));
};

const applySettings = (tab?: AuxiliaryDisplay) => {
  if (!tab) return;

  const { getPayloadFc, getAttribute } = mapperAction[tab];
  const payloadFc = getPayloadFc();
  const attribute = getAttribute();

  if (!payloadFc || !attribute) return;

  socketStore.sendMessageFc(payloadFc);

  toastStore.info(t('screen_apply_all_success'));
};

const setDefaultTab = () => {
  const { screenMode } = callStore.qrCodeInfo.options;
  tabActiveValue.value = screenMode ? 'SCREEN' : 'SUBTITLE';
};

onMounted(() => {
  setDefaultTab();
  userStore.setupRemotedUserPreferences('settings_screen_remote');
  userStore.setupRemotedUserPreferences('settings_subtitle_remote');
});
</script>

<style scoped lang="scss">
.container-settingsview {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  height: 100%;
  padding: 1rem 1rem 0 1rem;
  gap: 0.5rem;
  overflow-y: auto;
}

.container-settings {
  width: 100%;
  height: 100%;
}

.footer {
  position: sticky;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  bottom: 0;
  width: 100%;
  padding: 0.5rem 0 1rem 0;
  background-color: var(--surface-default);
}

.divider {
  background-color: var(--border-dim);
}
</style>
